import { format } from 'date-fns';
import { omit } from 'lodash-es';

import { dateInitFormats, isValidDate } from '@common/Util';

import { formatStringDateForRequest } from '../OrderSummaryDialog';

export const CustomFieldTypes = {
  DROPDOWN: 1,
  TEXT: 2,
  RADIO_BUTTON: 3,
  CHECKBOX: 4,
  TEXT_AREA: 5,
  DATE: 6,
  TIME: 7,
  DATE_TIME: 8,
  QUANTITY: 9
};

export const hasDefaultValue = [
  CustomFieldTypes.TEXT,
  CustomFieldTypes.TEXT_AREA,
  CustomFieldTypes.DATE,
  CustomFieldTypes.TIME,
  CustomFieldTypes.DATE_TIME,
  CustomFieldTypes.QUANTITY
];

export const createCustomFieldInitialValues = (customFields) => {
  if (!customFields) return {};

  return customFields.reduce((acc, customField) => {
    const temp = customField?.predefinedValues
      ?.filter((value) => value?.isDefaultValue)
      ?.map((value) => ({
        id: value?.id,
        value: value?.valueLabel,
        key: value?.valueKey
      }));

    acc[customField.id] = hasDefaultValue.includes(customField?.customFieldType?.id)
      ? (customField?.defaultValue?.value ?? '')
      : customField?.customFieldType?.id === CustomFieldTypes.CHECKBOX
        ? (temp ?? null)
        : customField?.customFieldType?.id === CustomFieldTypes.RADIO_BUTTON
          ? (temp?.[0] ?? null)
          : temp?.[0]?.id || '';
    return acc;
  }, {});
};

const additionalPropsBasedOnType = (customField, setFieldValue, fieldName) => {
  const options = customField?.predefinedValues?.map((value) => ({
    id: value?.id,
    value: value?.valueLabel,
    key: value?.valueKey
  }));

  switch (customField?.customFieldType?.id) {
    case CustomFieldTypes.DATE:
      return {
        component: 'datepicker',
        onChange: (value) => {
          isValidDate(value) &&
            setFieldValue(
              fieldName,
              value ? format(value, dateInitFormats.datePickerFormat, false) : null
            );
        }
      };
    case CustomFieldTypes.TIME:
      return {
        component: 'timepicker',
        ampm: false,
        format: dateInitFormats.time,
        onChange: (value) => {
          isValidDate(value) && setFieldValue(fieldName, format(value, dateInitFormats.time));
        }
      };
    case CustomFieldTypes.DATE_TIME:
      return {
        component: 'dateTimepicker',
        ampm: false,
        onChange: (value) => {
          isValidDate(value) &&
            setFieldValue(fieldName, format(value, dateInitFormats.dateTimePickerFormat, false));
        }
      };
    case CustomFieldTypes.RADIO_BUTTON:
      return {
        component: 'comboBox',
        options: options,
        onChange: (_, newValue) => setFieldValue(fieldName, newValue),
        multiple: false
      };
    case CustomFieldTypes.DROPDOWN:
      return {
        component: 'select',
        options: !customField?.isMandatory ? [{ id: 0, value: '' }, ...options] : options
      };
    case CustomFieldTypes.CHECKBOX:
      return {
        component: 'comboBox',
        options: options,
        onChange: (_, newValue) => setFieldValue(fieldName, newValue)
      };
    case CustomFieldTypes.TEXT:
      return {
        component: 'text'
      };
    case CustomFieldTypes.TEXT_AREA:
      return {
        component: 'text',
        multiline: true,
        rows: 3
      };
    case CustomFieldTypes.QUANTITY:
      return {
        component: 'text',
        type: 'number'
      };
    default:
      return {
        component: 'text'
      };
  }
};

export const customFieldNamePrefix = 'customFields';

export const mapCustomFieldsToFieldsConfig = (customField, setFieldValue, fieldName) => {
  return {
    label: customField?.label,
    placeholder: customField?.placeholder,
    name: fieldName,
    required: customField?.isMandatory,
    disabled: false,
    column: 6,
    ...additionalPropsBasedOnType(customField, setFieldValue, fieldName)
  };
};

export const filterInitialValues = (initialValues, eventData, index) => {
  let temp =
    index === 0 && eventData?.eventParticipant
      ? {
          hotelRequest: eventData?.eventParticipant?.hotelRequest,
          arrivalDate: eventData?.eventParticipant?.arrivalDate
            ? formatStringDateForRequest(eventData?.eventParticipant?.arrivalDate, true)
            : format(eventData?.startDateTime, dateInitFormats.datePickerFormat) || new Date(),
          departureDate: eventData?.eventParticipant?.departureDate
            ? formatStringDateForRequest(eventData?.eventParticipant?.departureDate, true)
            : format(eventData?.endDateTime, dateInitFormats.datePickerFormat) || new Date(),
          vegan: eventData?.eventParticipant?.isVegan,
          vegetarian: eventData?.eventParticipant?.isVegetarian,
          handicapped: eventData?.eventParticipant?.isHandicap,
          allergies: eventData?.eventParticipant?.allergies?.map((allergy) => allergy?.id) || []
        }
      : {
          ...initialValues,
          arrivalDate:
            format(eventData?.startDateTime, dateInitFormats.datePickerFormat) || new Date(),
          departureDate:
            format(eventData?.endDateTime, dateInitFormats.datePickerFormat) || new Date()
        };
  if (!eventData?.enableHotelRequest) {
    temp = omit(temp, 'hotelRequest', 'arrivalDate', 'departureDate');
  }
  if (!eventData?.questionnaireEnabled) {
    temp = omit(temp, 'vegan', 'vegetarian', 'handicapped', 'allergies');
  }
  return temp;
};
